// src/components/TradingViewAuth.js
// TradingView Authentication Integration for Live Data
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ExternalLink, User, CheckCircle, AlertCircle, LogOut, Zap, RefreshCw, X } from 'lucide-react';

// TradingView Auth Context
const TradingViewAuthContext = React.createContext();

export const useTradingViewAuth = () => {
  const context = React.useContext(TradingViewAuthContext);
  if (!context) {
    throw new Error('useTradingViewAuth must be used within TradingViewAuthProvider');
  }
  return context;
};

// TradingView Auth Provider
export const TradingViewAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [subscriptionLevel, setSubscriptionLevel] = useState('free');
  const [authWindow, setAuthWindow] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check for existing TradingView session
  useEffect(() => {
    checkExistingAuth();
  }, []);

  const checkExistingAuth = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/tradingview/check-auth', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const authData = await response.json();
        if (authData.authenticated) {
          setIsAuthenticated(true);
          setUserInfo(authData.user);
          setSubscriptionLevel(authData.subscription || 'free');
          console.log('✅ Existing TradingView session found:', authData.user.username);
        }
      }
    } catch (error) {
      console.error('Error checking existing TradingView auth:', error);
    } finally {
      setLoading(false);
    }
  };

  const initiateLogin = useCallback(() => {
    setLoading(true);
    
    // Create popup for TradingView login
    const authUrl = 'https://www.tradingview.com/accounts/signin/';
    const popup = window.open(
      authUrl,
      'tradingview_auth',
      'width=500,height=700,scrollbars=yes,resizable=yes,centerscreen=yes'
    );
    
    setAuthWindow(popup);

    // Monitor popup for closure
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        setAuthWindow(null);
        setLoading(false);
        
        // Check if auth was successful after popup closes
        setTimeout(checkAuthCompletion, 2000);
      }
    }, 1000);

    // Clean up if component unmounts
    return () => {
      clearInterval(checkClosed);
      if (popup && !popup.closed) {
        popup.close();
      }
      setLoading(false);
    };
  }, []);

  const checkAuthCompletion = async () => {
    try {
      console.log('🔍 Checking TradingView auth completion...');
      
      const response = await fetch('/api/tradingview/check-auth', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const authData = await response.json();
        if (authData.authenticated) {
          handleAuthSuccess(authData);
        } else {
          console.log('❌ TradingView authentication not completed');
        }
      }
    } catch (error) {
      console.error('Error checking auth completion:', error);
    }
  };

  const handleAuthSuccess = (authData) => {
    console.log('✅ TradingView authentication successful:', authData);
    setIsAuthenticated(true);
    setUserInfo(authData.user);
    setSubscriptionLevel(authData.subscription || 'free');
    setLoading(false);
  };

  const logout = async () => {
    try {
      setLoading(true);
      
      const response = await fetch('/api/tradingview/logout', {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        setIsAuthenticated(false);
        setUserInfo(null);
        setSubscriptionLevel('free');
        console.log('✅ TradingView logout successful');
      }
    } catch (error) {
      console.error('Error logging out from TradingView:', error);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    isAuthenticated,
    userInfo,
    subscriptionLevel,
    loading,
    initiateLogin,
    logout,
    hasRealTimeData: subscriptionLevel !== 'free'
  };

  return (
    <TradingViewAuthContext.Provider value={value}>
      {children}
    </TradingViewAuthContext.Provider>
  );
};

// TradingView Login Component
export const TradingViewAuthButton = ({ darkMode, className = "" }) => {
  const { 
    isAuthenticated, 
    userInfo, 
    subscriptionLevel, 
    loading, 
    initiateLogin, 
    logout, 
    hasRealTimeData 
  } = useTradingViewAuth();

  if (loading) {
    return (
      <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${
        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
      } ${className}`}>
        <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
        <span className="text-sm">Connecting...</span>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${
        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
      } ${className}`}>
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${hasRealTimeData ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'}`} />
          <User className="w-4 h-4 text-blue-500" />
          <div className="flex flex-col">
            <span className={`text-xs font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {userInfo?.username || 'TradingView User'}
            </span>
            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {subscriptionLevel === 'free' ? 'Free Plan' : `${subscriptionLevel.toUpperCase()} Plan`}
            </span>
          </div>
        </div>
        <button
          onClick={logout}
          disabled={loading}
          className={`p-1 rounded hover:bg-opacity-20 transition-colors ${
            darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
          }`}
          title="Logout from TradingView"
        >
          <LogOut className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={initiateLogin}
      disabled={loading}
      className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors ${
        darkMode
          ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
      } ${className} ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
      title="Login to TradingView for real-time data"
    >
      <ExternalLink className="w-4 h-4" />
      <span className="text-sm">Connect TradingView</span>
    </button>
  );
};

// Enhanced TradingView Modal with Authentication
export const EnhancedTradingViewModal = ({ ticker, isOpen, onClose, darkMode }) => {
  const { isAuthenticated, hasRealTimeData, subscriptionLevel } = useTradingViewAuth();
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [sessionToggle, setSessionToggle] = useState('extended');
  const [dataSource, setDataSource] = useState('delayed');
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !ticker) {
      setIsChartLoading(true);
      return;
    }

    console.log('🔄 Loading TradingView chart:', {
      ticker,
      authenticated: isAuthenticated,
      hasRealTime: hasRealTimeData,
      subscription: subscriptionLevel,
      session: sessionToggle
    });

    const validTicker = /^[A-Z0-9.-]+$/.test(ticker);
    if (!validTicker) {
      console.error(`❌ Invalid ticker format: ${ticker}`);
      setIsChartLoading(false);
      return;
    }

    const container = containerRef.current;
    if (!container) {
      console.error('❌ Chart container not found');
      setIsChartLoading(false);
      return;
    }

    // Clear existing content
    container.innerHTML = '';

    // Create TradingView widget container
    const widgetContainer = document.createElement('div');
    widgetContainer.className = 'tradingview-widget-container';
    widgetContainer.style.height = '100%';
    widgetContainer.style.width = '100%';

    const widgetDiv = document.createElement('div');
    widgetDiv.className = 'tradingview-widget-container__widget';
    widgetDiv.style.height = 'calc(100% - 32px)';
    widgetDiv.style.width = '100%';

    widgetContainer.appendChild(widgetDiv);
    container.appendChild(widgetContainer);

    // Create script element
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.async = true;

    // Enhanced configuration based on authentication
    const config = {
      "autosize": true,
      "symbol": ticker,
      "interval": hasRealTimeData ? "1" : "5",
      "timezone": "America/New_York",
      "theme": darkMode ? "dark" : "light",
      "style": "1",
      "locale": "en",
      "toolbar_bg": darkMode ? "#1E1E1E" : "#FFFFFF",
      "enable_publishing": false,
      "backgroundColor": darkMode ? "rgba(19, 23, 34, 1)" : "rgba(255, 255, 255, 1)",
      "gridColor": darkMode ? "rgba(42, 46, 57, 1)" : "rgba(232, 232, 232, 1)",
      "allow_symbol_change": true,
      "save_image": false,
      "calendar": false,
      "support_host": "https://www.tradingview.com",
      
      // Authentication-aware features
      "show_popup_button": true,
      "popup_width": "1000",
      "popup_height": "650",
      
      // Enhanced features for authenticated users
      "enabled_features": isAuthenticated ? [
        "study_templates",
        "use_localstorage_for_settings", 
        "save_chart_properties_to_local_storage",
        "chart_property_page_style",
        "show_chart_property_page",
        "chart_crosshair_menu",
        "left_toolbar",
        "header_widget",
        "timeframes_toolbar",
        "edit_buttons_in_legend",
        "context_menus",
        "control_bar",
        "widget_logo"
      ] : [
        "left_toolbar",
        "header_widget", 
        "timeframes_toolbar"
      ],
      
      "disabled_features": [
        "volume_force_overlay"
      ],
      
      // Session configuration
      "extended_session": sessionToggle === 'extended',
      "show_session_breaks": true,
      "session_template": sessionToggle === 'extended' ? "extended" : "regular",
      
      "withdateranges": true,
      "range": hasRealTimeData ? "1D" : "5D",
      "hide_top_toolbar": false,
      "hide_legend": false,
      "hide_side_toolbar": false,
      "details": true,
      "hotlist": true,
      "calendar": true,
      
      // Enhanced overrides
      "overrides": {
        "paneProperties.background": darkMode ? "#131722" : "#FFFFFF",
        "paneProperties.backgroundType": "solid",
        "mainSeriesProperties.candleStyle.upColor": "#00C851",
        "mainSeriesProperties.candleStyle.downColor": "#FF4444",
        "mainSeriesProperties.candleStyle.drawWick": true,
        "mainSeriesProperties.candleStyle.drawBorder": true,
        "mainSeriesProperties.candleStyle.borderUpColor": "#00C851",
        "mainSeriesProperties.candleStyle.borderDownColor": "#FF4444",
        "mainSeriesProperties.candleStyle.wickUpColor": "#00C851",
        "mainSeriesProperties.candleStyle.wickDownColor": "#FF4444",
        "mainSeriesProperties.extendedHours": sessionToggle === 'extended',
        "volumePaneSize": "medium",
        "scalesProperties.showSeriesLastValue": true,
        "scalesProperties.showSeriesOHLC": hasRealTimeData,
        "scalesProperties.showBarChange": hasRealTimeData,
        "paneProperties.vertGridProperties.color": darkMode ? "#2a2e39" : "#e8e8e8",
        "paneProperties.horzGridProperties.color": darkMode ? "#2a2e39" : "#e8e8e8",
        "mainSeriesProperties.showCountdown": hasRealTimeData,
        "mainSeriesProperties.showPriceLine": hasRealTimeData,
        "mainSeriesProperties.priceLineColor": "#2196F3",
        "timeScale.rightOffset": 5,
        "timeScale.rightBarStaysOnScroll": true
      },
      
      // Studies configuration
      "studies": [
        {
          "id": "MACD@tv-basicstudies",
          "name": "MACD",
          "inputs": {
            "fastLength": 12,
            "slowLength": 26,
            "signalLength": 9
          }
        },
        {
          "id": "Volume@tv-basicstudies", 
          "name": "Volume"
        }
      ],
      
      "loading_screen": {
        "backgroundColor": darkMode ? "#131722" : "#FFFFFF",
        "foregroundColor": darkMode ? "#787B86" : "#787B86"
      }
    };

    // Add VWAP for premium users
    if (subscriptionLevel === 'premium' || subscriptionLevel === 'pro_plus') {
      config.studies.push({
        "id": "VWAP@tv-basicstudies",
        "name": "VWAP"
      });
    }

    script.innerHTML = JSON.stringify(config);

    script.onload = () => {
      console.log('✅ TradingView chart loaded successfully:', ticker);
      setIsChartLoading(false);
      setDataSource(hasRealTimeData ? 'live' : 'delayed');
    };

    script.onerror = (error) => {
      console.error('❌ Failed to load TradingView script:', error);
      setIsChartLoading(false);
      setDataSource('error');
    };

    widgetContainer.appendChild(script);

    // Cleanup function
    return () => {
      if (container) {
        container.innerHTML = '';
      }
      setIsChartLoading(true);
      setDataSource('delayed');
    };
  }, [isOpen, ticker, darkMode, sessionToggle, isAuthenticated, hasRealTimeData, subscriptionLevel]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className={`relative w-11/12 h-5/6 max-w-6xl rounded-lg shadow-xl ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Enhanced Modal Header */}
        <div className={`flex justify-between items-center p-4 border-b ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center space-x-4">
            <h2 className={`text-lg font-semibold ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {ticker} - {hasRealTimeData ? 'Live' : 'Delayed'} Chart
            </h2>
            
            {/* Data Source & Auth Status */}
            <div className="flex items-center space-x-3">
              {/* Data Status */}
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  dataSource === 'live' ? 'bg-green-500 animate-pulse' :
                  dataSource === 'delayed' ? 'bg-yellow-500' :
                  'bg-red-500'
                }`} />
                <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {dataSource === 'live' ? 'Real-time Data' :
                   dataSource === 'delayed' ? '15min Delayed' :
                   'Data Error'}
                </span>
              </div>

              {/* TradingView Auth Status */}
              {isAuthenticated ? (
                <div className="flex items-center space-x-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className={`text-xs ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                    TradingView Connected
                  </span>
                </div>
              ) : (
                <div className="flex items-center space-x-1">
                  <AlertCircle className="w-4 h-4 text-yellow-500" />
                  <span className={`text-xs ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                    Not Connected
                  </span>
                </div>
              )}
            </div>
            
            {/* Session Toggle */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setSessionToggle('regular')}
                className={`px-3 py-1 text-xs rounded ${
                  sessionToggle === 'regular'
                    ? 'bg-blue-500 text-white'
                    : darkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Regular
              </button>
              <button
                onClick={() => setSessionToggle('extended')}
                className={`px-3 py-1 text-xs rounded ${
                  sessionToggle === 'extended'
                    ? 'bg-green-500 text-white'
                    : darkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Extended
              </button>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className={`p-1 rounded-lg transition-colors ${
              darkMode
                ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Chart Container */}
        <div className="relative" style={{ height: 'calc(100% - 60px)' }}>
          {/* Auth Upgrade Banner */}
          {!isAuthenticated && (
            <div className={`absolute top-2 left-1/2 transform -translate-x-1/2 z-20 ${
              darkMode ? 'bg-blue-900' : 'bg-blue-50'
            } border border-blue-200 rounded-lg p-2 flex items-center space-x-2`}>
              <Zap className="w-4 h-4 text-blue-500" />
              <span className={`text-xs ${darkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                Connect your TradingView account for real-time data
              </span>
              <TradingViewAuthButton darkMode={darkMode} className="ml-2" />
            </div>
          )}

          {isChartLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-opacity-75 z-10">
              <div className="flex flex-col items-center">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mb-2" />
                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Loading {hasRealTimeData ? 'live' : 'delayed'} chart for {ticker}...
                </span>
              </div>
            </div>
          )}
          
          <div
            ref={containerRef}
            className="w-full h-full"
            style={{ minHeight: '400px' }}
          />
        </div>
      </div>
    </div>
  );
};